import { useCallback } from "react";
import { getDocumentUrl } from "../api/queries/files.queries";

export interface DocumentFileInfo {
	bucket: string;
	key: string;
	name: string;
	mimeType: string;
}

export const useDocumentToFile = () => {
	const convertDocumentToFile = useCallback(
		async <T>(
			document: T,
			extractInfo: (doc: T) => DocumentFileInfo,
		): Promise<File> => {
			const fileInfo = extractInfo(document);
			const url = await getDocumentUrl({
				bucket: fileInfo.bucket,
				key: fileInfo.key,
			});

			const response = await fetch(url);
			if (!response.ok) {
				throw new Error(`Failed to fetch document: ${response.statusText}`);
			}

			const blob = await response.blob();

			return new File([blob], fileInfo.name, {
				type: fileInfo.mimeType,
			});
		},
		[],
	);

	const convertDocumentsToFiles = useCallback(
		async <T>(
			documents: T[],
			extractInfo: (doc: T) => DocumentFileInfo,
		): Promise<File[]> => {
			const filePromises = documents.map((doc) =>
				convertDocumentToFile(doc, extractInfo),
			);
			return Promise.all(filePromises);
		},
		[convertDocumentToFile],
	);

	return {
		convertDocumentToFile,
		convertDocumentsToFiles,
	};
};
